/**
 * Dev Toolbar — Dataset switcher for E2E testing.
 * Only visible in development mode.
 */

import { useState } from 'react';
import { useAppStore } from '@/stores/useAppStore';
import { useExpenseStore } from '@/stores/useExpenseStore';
import { TEST_DATASETS, getAllDatasetKeys, type TestDatasetKey } from '@/lib/test-datasets';
import { Bug, ChevronUp } from 'lucide-react';

export function DevToolbar() {
  const [expanded, setExpanded] = useState(false);
  const { setUser, setActiveOrg, setActiveEntity, setEntityMemberships } = useAppStore();
  const expenseStore = useExpenseStore();
  const [activeDataset, setActiveDataset] = useState<TestDatasetKey>('physician');

  if (!import.meta.env.DEV) return null;

  const switchDataset = (key: TestDatasetKey) => {
    const ds = TEST_DATASETS[key];

    // Update app store
    setUser(ds.users[0]);
    setActiveOrg(ds.org);
    setActiveEntity(ds.entities[0]);
    setEntityMemberships(ds.memberships);

    // Replace expense store
    // Clear and re-add
    expenseStore.replaceAll(ds.expenses);

    setActiveDataset(key);
  };

  return (
    <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 z-50">
      {expanded && (
        <div className="bg-slate-900/95 backdrop-blur-sm border-t border-slate-700 px-4 py-3">
          <div className="max-w-[1200px] mx-auto">
            <p className="text-xs text-slate-400 mb-2 font-mono">Test Dataset Switcher</p>
            <div className="flex gap-2 flex-wrap">
              {getAllDatasetKeys().map((key) => {
                const ds = TEST_DATASETS[key];
                const isActive = activeDataset === key;
                return (
                  <button
                    key={key}
                    onClick={() => switchDataset(key)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-600'
                    }`}
                  >
                    <p className="font-semibold">{ds.label}</p>
                    <p className="opacity-70 mt-0.5">{ds.expenses.length} expenses &middot; {ds.entities[0].taxElection}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-4 mt-2 text-[10px] text-slate-500 font-mono">
              <span>Org: {TEST_DATASETS[activeDataset].org.name}</span>
              <span>Entity: {TEST_DATASETS[activeDataset].entities[0].legalName}</span>
              <span>Tax: {TEST_DATASETS[activeDataset].entities[0].taxElection}</span>
              <span>Comp: {TEST_DATASETS[activeDataset].entities[0].compensationModel}</span>
              <span>Mode: {TEST_DATASETS[activeDataset].org.displayMode}</span>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setExpanded(!expanded)}
        className="absolute bottom-0 right-4 lg:right-8 -translate-y-0 bg-slate-800 border border-slate-600 text-slate-400 hover:text-white px-3 py-1 rounded-t-lg text-xs font-mono flex items-center gap-1 transition-colors"
      >
        <Bug className="w-3 h-3" />
        Test: {activeDataset}
        <ChevronUp className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
}
